import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { TwilioPhoneNumberType } from 'constants/twilio/twilioPhoneNumberType';
import {
  TwilioAvailablePhoneNumber,
  TwilioAvailablePhoneNumbersFilter,
} from 'types/twilio/twilioAvailablePhoneNumber';
import { usePurchaseTwilioPhoneNumber } from '../mutations/usePurchaseTwilioPhoneNumber';
import { useGetTwilioAvailablePhoneNumbers } from '../queries/useGetTwilioAvailablePhoneNumber';

interface TwilioAvailablePhoneNumbersProps {
  messagingServiceId: string | null | undefined;
  businessId: number;
  filter: TwilioAvailablePhoneNumbersFilter;
  onPurchase?: () => void;
}

export const TwilioAvailablePhoneNumbers = ({
  businessId,
  messagingServiceId,
  filter,
  onPurchase,
}: TwilioAvailablePhoneNumbersProps) => {
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string | null>(null);

  const { data: phoneNumberResponseBody, isLoading: isFetchingAvailablePhoneNumbersLoading } =
    useGetTwilioAvailablePhoneNumbers(businessId, filter);

  const { mutateAsync: purchasePhoneNumber, isLoading: isPurchasePhoneNumberLoading } =
    usePurchaseTwilioPhoneNumber({ successCallback: onPurchase });

  const handleToggle = (phoneNumber: TwilioAvailablePhoneNumber['phoneNumber']) => () => {
    setSelectedPhoneNumber(selectedPhoneNumber === phoneNumber ? null : phoneNumber);
  };

  const isButtonDisabled =
    !selectedPhoneNumber || !messagingServiceId || isPurchasePhoneNumberLoading;

  const handlePhoneNumberPurchaseClickEvent = async (phoneNumberType: TwilioPhoneNumberType) => {
    await purchasePhoneNumber({
      businessId,
      data: {
        phoneNumberType,
        messagingServiceId: messagingServiceId!,
        phoneNumber: selectedPhoneNumber!,
      },
    });
  };

  return (
    <div>
      <h4 className="mt-3 mb-4 text-center">Available Phone Numbers</h4>

      {isFetchingAvailablePhoneNumbersLoading && <CentsLoader />}

      <List sx={{ maxHeight: 300, overflow: 'auto' }}>
        {phoneNumberResponseBody?.phoneNumbers.map(({ phoneNumber }) => {
          const labelId = `checkbox-list-label-${phoneNumber}`;

          return (
            <ListItem key={phoneNumber} disablePadding>
              <ListItemButton role={undefined} onClick={handleToggle(phoneNumber)} dense>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedPhoneNumber === phoneNumber}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': labelId }}
                  />
                </ListItemIcon>

                <ListItemText id={labelId} primary={phoneNumber} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <p className="text-center text-danger">
        It is important to buy a marketing phone number first if it hasn't been purchased yet
      </p>

      <div className="d-flex justify-content-center mt-2 mb-3">
        <Button
          variant="outlined"
          onClick={() => handlePhoneNumberPurchaseClickEvent(TwilioPhoneNumberType.MARKETING)}
          disabled={isButtonDisabled}
        >
          Purchase marketing phone number
        </Button>

        <Button
          variant="outlined"
          onClick={() => handlePhoneNumberPurchaseClickEvent(TwilioPhoneNumberType.TRANSACTIONAL)}
          disabled={isButtonDisabled}
        >
          Purchase transactional phone number
        </Button>
      </div>
    </div>
  );
};
